import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthDataService, AuthService } from '@nomoh-monorepo/data-access-auth';
import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
import { removerEspacos } from '@nomoh-monorepo/shared-angular-util-tools';
import {
  APP_API_NAME,
  EnvironmentService,
} from '@nomoh-monorepo/shared-data-access-environment';
import {
  stringApenasNumeros,
  telefoneValidator,
} from '@nomoh-monorepo/shared-util-tools';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FormFieldComponent } from '../../form-field/form-field.component';
import { PhoneInputComponent } from '../../phone-input/phone-input.component';
import { AppConfig } from '../models/app-config.interface';

@Component({
  selector: 'lib-login',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    CommonModule,
    ButtonModule,
    FormFieldComponent,
    PhoneInputComponent,
    FloatLabelModule,
    InputTextModule,
    PasswordModule,
    SelectButtonModule,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  appConfig!: AppConfig;
  form: FormGroup = {} as FormGroup;
  $isBotaoSalvar = signal<boolean>(false);
  $loadingLogin = signal<boolean>(false);
  valueLogin = true;

  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  authDataService = inject(AuthDataService);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);
  private notificacaoService = inject(NotificacaoService);
  private environment = inject(EnvironmentService);
  private appApiName = inject(APP_API_NAME);

  ngOnInit() {
    this.appConfig = this.route.snapshot.data['appConfig'];
    if (!this.appConfig) {
      throw new Error('AppConfig não encontrado em route.data');
    }

    this.criarFormulario();
    this.verificarParametrosUrl();
  }

  private criarFormulario(): void {
    this.form = this.fb.group({
      email: [null],
      telefone: [null, [Validators.required, telefoneValidator()]],
      senha: [null, [Validators.required]],
    });
  }

  alterarOpcaoLogin(useTelefone: boolean): void {
    if (this.valueLogin === useTelefone) return;

    this.valueLogin = useTelefone;
    this.configurarValidacoes();
  }

  toggleLoginType(): void {
    this.valueLogin = !this.valueLogin;
    this.configurarValidacoes();
  }

  private configurarValidacoes(): void {
    const emailControl = this.form.get('email');
    const telefoneControl = this.form.get('telefone');

    emailControl?.setValue(null);
    telefoneControl?.setValue(null);
    emailControl?.setErrors(null);
    telefoneControl?.setErrors(null);

    if (this.valueLogin) {
      emailControl?.clearValidators();
      telefoneControl?.setValidators([Validators.required, telefoneValidator()]);
    } else {
      telefoneControl?.clearValidators();
      emailControl?.setValidators([Validators.required, Validators.email]);
    }

    emailControl?.updateValueAndValidity();
    telefoneControl?.updateValueAndValidity();

    this.$isBotaoSalvar.set(false);
  }

  onTelefoneChange(value: string): void {
    const ctrl = this.form.get('telefone');
    ctrl?.setValue(value);
    ctrl?.markAsDirty();
    ctrl?.markAsTouched();
  }

  validarClasseErro(campo: string): string {
    return this.validarCampo(campo) ? 'cs-input--error' : '';
  }

  validarCampo(campo: string): boolean {
    const control = this.form.get(campo);
    return !!(control?.errors && (control?.dirty || control?.touched));
  }

  async onSubmit() {
    if (!this.form.valid || this.$loadingLogin()) {
      return;
    }

    this.$loadingLogin.set(true);
    removerEspacos(this.form);
    const { email, senha, telefone } = this.form.getRawValue();

    const resultado = await this.authService.login({
      email: email ?? '',
      telefone: stringApenasNumeros(telefone) ?? '',
      senha: senha ?? '',
      sistema: this.appConfig.sistema,
    });

    if (resultado === 'SELECIONAR_CONTEXTO') {
      await this.router.navigate(['/selecionar-contexto']);
    } else if (resultado === 'AUTENTICADO') {
      await this.router.navigate([this.appConfig.dashboardRoute]);
    } else {
      this.$loadingLogin.set(false);
    }
  }

  loginComGoogle(): void {
    this.iniciarOAuth('google');
  }

  loginComMicrosoft(): void {
    this.iniciarOAuth('microsoft');
  }

  private iniciarOAuth(provider: 'google' | 'microsoft'): void {
    const apiUrl = this.environment.getApiUrl(this.appApiName);
    const returnUrl = `${window.location.origin}/login`;
    const sistema = this.appConfig.sistema;
    window.location.href = `${apiUrl}/auth/oauth/${provider}?returnUrl=${encodeURIComponent(
      returnUrl
    )}&sistema=${encodeURIComponent(sistema)}`;
  }

  navegarHome(): void {
    this.router.navigateByUrl(this.appConfig.dashboardRoute || '/');
  }

  recuperarSenha() {
    this.router.navigate(['/recuperar-senha']);
  }

  cadastrar() {
    this.router.navigate(['/cadastrar']);
  }

  private verificarParametrosUrl(): void {
    this.route.queryParamMap.subscribe(async (params) => {
      const token = params.get('token') || null;
      const pessoaId = params.get('id') || null;
      const oauthSuccess = params.get('oauthSuccess');
      const oauthError = params.get('oauthError');

      if (oauthError) {
        const detalhe = decodeURIComponent(oauthError);
        this.notificacaoService.notificacaoError(
          detalhe && detalhe !== 'denied'
            ? `Conta social: ${detalhe}`
            : 'Não foi possível entrar com a conta social.'
        );
        await this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true,
        });
        return;
      }

      if (oauthSuccess) {
        this.$loadingLogin.set(true);
        try {
          await this.authDataService.checkSession();
          if (this.authDataService.redirecionarSePlanoBloqueado()) {
            return;
          }
          if (this.authDataService.$isAuthenticated()) {
            await this.router.navigate([this.appConfig.dashboardRoute]);
            return;
          }
          this.notificacaoService.notificacaoError(
            'Sessão social não estabelecida.'
          );
        } finally {
          this.$loadingLogin.set(false);
        }
        return;
      }

      if (token && pessoaId) {
        this.authDataService.confirmarConta(token, pessoaId).subscribe({
          next: () => {
            this.notificacaoService.notificacaoSuccess(
              'Conta confirmada com sucesso!'
            );
          },
          error: (error) => {
            console.error('Erro ao confirmar conta:', error);
          },
        });
      }
    });
  }
}
